/**
 * AreaProvider — leitet den aktiven Bereich (patient | klinik | ambulant |
 * admin) aus Pfad / Demo / Org-`setting_type` ab (Precedence siehe
 * `deriveArea` in `@/features/area`) und spiegelt ihn als `.area-*`-Klasse
 * aufs <html>-Element. Damit greift automatisch die Bereichs-Akzentfarbe
 * aus `src/index.css`.
 *
 * Ersetzt die frühere `<ModeSync />`-Komponente und die `mode`-Achse im
 * ThemeContext. Muss innerhalb des `<BrowserRouter>` (nutzt `useLocation`)
 * und nach Auth/Demo (nutzt `useFeatures` + `useDemo`) gemountet werden.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';

import { useDemo } from '@/contexts/DemoContext';
import { useFeatures } from '@/hooks/useFeatures';
import { AREAS, deriveArea, type Area } from '@/features/area';

type Ctx = { area: Area };

const AreaContext = createContext<Ctx | null>(null);

function applyAreaClass(area: Area) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  for (const candidate of AREAS) {
    root.classList.toggle(`area-${candidate}`, candidate === area);
  }
}

export function AreaProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { isDemo, demoRole, demoSettingType } = useDemo();
  const { settingType } = useFeatures();

  const area = useMemo(
    () =>
      deriveArea({
        pathname: location.pathname,
        search: location.search,
        isDemo,
        demoRole,
        demoSettingType,
        settingType,
      }),
    [location.pathname, location.search, isDemo, demoRole, demoSettingType, settingType],
  );

  useEffect(() => {
    applyAreaClass(area);
  }, [area]);

  const value = useMemo<Ctx>(() => ({ area }), [area]);
  return <AreaContext.Provider value={value}>{children}</AreaContext.Provider>;
}

export function useArea(): Area {
  const ctx = useContext(AreaContext);
  if (!ctx) throw new Error('useArea must be used inside <AreaProvider>');
  return ctx.area;
}
